import { makeAutoObservable, action, computed, reaction } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    apiDateToFormDate,
    checkIsArray,
    divideBy100,
    getFieldsFromUsageConditions,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import {
    isDateAfter,
    isDateBefore,
    isMultiselectNotEmpty,
    isNumericBetween,
    isNumericRelationWithNumber,
    isNumericWholeNumber,
    validateIf,
    validateRelativeExpiryDays,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { SportsPickerState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/SportsPickerState.state';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { Common } from 'src/AppState/Common';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';

type SportsbookSelectionFormType = {
    'bet-type': string;
    'bet-min-odds': string;
};

export class SportsBookSectionState {
    public readonly betTypeForm: FormInputState<string, string>;
    public readonly rewardValueForm: FormInputState<string, string>;
    public readonly minOddsFrom: FormInputState<string, string>;
    public readonly expiryForm: FormInputState<string, string>;
    public readonly relativeExpiryDays: FormInputState<string, string>;
    public readonly endDateTimeForm: FormInputState<string, string>;
    public readonly installmentCountForm: FormInputState<string, string>;
    public readonly rewardIntervalForm: FormInputState<string, string>;
    public readonly scheduledRewardForm: FormInputState<boolean, boolean>;
    public readonly rewardDateForm: FormInputState<string, string>;

    public readonly form: FormModel<SportsbookSelectionFormType>;

    private readonly noEmptyValidationMessage = 'Required field';
    private readonly defaultExpiryType = 'absolute-expiry';

    public readonly sportsPickerState: SportsPickerState;

    public constructor(
        public readonly common: Common,
        public readonly campaignId: string | undefined
    ) {
        makeAutoObservable(this);

        this.betTypeForm = FormInputState.new('').map(isMultiselectNotEmpty(false, this.noEmptyValidationMessage));
        this.rewardValueForm = FormInputState.new('')
            .map(validateNotEmpty('Min value is £1'))
            .map(isNumericRelationWithNumber(1, 'more', true, () => true, 'Min value is £1'));
        this.minOddsFrom = FormInputState.new('')
            .map(validateNotEmpty('Max value is 100.00'))
            .map(isNumericRelationWithNumber(100, 'less', true, () => true, 'Max value is 100.00'))
            .map(isNumericRelationWithNumber(1, 'more', true, () => true, 'Min value is 1'));
        this.expiryForm = FormInputState.new(this.defaultExpiryType).map(
            validateNotEmpty(this.noEmptyValidationMessage)
        );
        this.relativeExpiryDays = FormInputState.new('').map(validateRelativeExpiryDays(() => this.expiryForm.value));

        this.endDateTimeForm = FormInputState.new('')
            .map((value) => {
                if (this.expiryForm.value === 'absolute-expiry') {
                    return validateNotEmpty(this.noEmptyValidationMessage)(value);
                }
                return Result.createOk(value);
            })
            .map((value) => {
                if (this.expiryForm.value !== 'absolute-expiry') {
                    return Result.createOk(value);
                }

                const generalDetailsState = CampaignCreatorState.get(this.common, this.campaignId).generalDetailsState;
                return isDateAfter(
                    () => generalDetailsState.expiryAtForm.value,
                    false,
                    'The expiration date must be after campaign end date.'
                )(value);
            });

        this.installmentCountForm = FormInputState.new('')
            .map(
                isNumericBetween({
                    from: 1,
                    to: 999,
                    optional: true,
                    message: 'Enter a number between 1 and 999',
                })
            )
            .map(isNumericWholeNumber(true, 'Enter a whole number between 1 and 999'))
            .map((value) => {
                return validateIf(
                    () => this.rewardIntervalForm.value.length > 0,
                    () => validateNotEmpty('Required field if reward interval has been set')(value)
                );
            });
        this.rewardIntervalForm = FormInputState.new('')
            .map(
                isNumericBetween({
                    from: 1,
                    to: 999,
                    optional: true,
                    message: 'Enter a number between 1 and 999',
                })
            )
            .map(isNumericWholeNumber(true, 'Enter a whole number between 1 and 999'))
            .map((value) => {
                return validateIf(
                    () => this.installmentCountForm.value.length > 0,
                    () => validateNotEmpty('Required field if installment count has been set')(value)
                );
            });
        this.scheduledRewardForm = FormInputState.new(false);
        this.rewardDateForm = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.scheduledReward,
                    () => validateNotEmpty(this.noEmptyValidationMessage)(value)
                );
            })
            .map(isDateAfter(() => new Date().toString(), false, "Reward date can't be in the past"))
            .map((value) => {
                return validateIf(
                    () => this.scheduledReward,
                    () => {
                        if (this.expiryForm.value !== 'absolute-expiry') {
                            return Result.createOk(value);
                        }

                        return isDateBefore(
                            () => this.endDateTimeForm.value,
                            false,
                            'The Reward date must be before the Expiration date.'
                        )(value);
                    }
                );
            });

        this.form = FormModel.group({
            'bet-type': this.betTypeForm,
            'reward-value': this.rewardValueForm,
            'bet-min-odds': this.minOddsFrom,
            expiry: this.expiryForm,
            'relative-expiry-days': this.relativeExpiryDays,
            'end-date-time': this.endDateTimeForm,
            'installment-count': this.installmentCountForm,
            'reward-interval': this.rewardIntervalForm,
            'scheduled-reward': this.scheduledRewardForm,
            'reward-date': this.rewardDateForm,
        });

        this.sportsPickerState = new SportsPickerState($appState.common);

        reaction(
            () => this.expiryForm.value,
            (value) => {
                if (value === 'absolute-expiry') {
                    this.relativeExpiryDays.setValue('');
                } else if (value === 'relative-expiry') {
                    this.endDateTimeForm.setValue('');
                }
            }
        );
    }

    @computed public get scheduledReward(): boolean {
        return this.scheduledRewardForm.value;
    }

    @action public validateForm(): void {
        this.form.setAsVisited();
    }

    @computed public get isFormValid(): boolean {
        return this.form.result.value.type === 'ok';
    }

    @computed public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public isTouched(): boolean {
        return this.form.isTouched() || this.sportsPickerState.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
        this.sportsPickerState.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    @action public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        const rewardType = campaignModel.bonusRewards?.[0]?.rewardType;
        const transformedCriteria = getFieldsFromUsageConditions(campaignModel);

        if (rewardType === 'free-bet') {
            const expiryDate = apiDateToFormDate(campaignModel.bonusRewards?.[0]?.expiryAt);
            this.endDateTimeForm.setValue(expiryDate);

            const relativeExpiryDays = campaignModel.bonusRewardRelativeExpiryDays;
            this.relativeExpiryDays.setValue(relativeExpiryDays === null ? '' : relativeExpiryDays.toString());

            const hasRelativeExpiry = relativeExpiryDays !== null;
            this.expiryForm.setValue(hasRelativeExpiry ? 'relative-expiry' : this.defaultExpiryType);

            this.installmentCountForm.setValue(campaignModel.installmentCount?.toString() ?? '');
            this.rewardIntervalForm.setValue(campaignModel.rewardInterval?.toString() ?? '');

            this.scheduledRewardForm.setValue(campaignModel.bonusRewards?.[0]?.scheduledReward ?? false);
            const rewardDate = apiDateToFormDate(campaignModel.bonusRewards?.[0]?.rewardDate);
            this.rewardDateForm.setValue(rewardDate);
        }

        await this.sportsPickerState.setInitValues({
            sportIds: checkIsArray(transformedCriteria?.['sport']) ?? [],
            competitionIds: checkIsArray(transformedCriteria?.['competition']) ?? [],
            eventIds: checkIsArray(transformedCriteria?.['event']) ?? [],
            marketIds: checkIsArray(transformedCriteria?.['market']) ?? [],
        });

        this.betTypeForm.setValue(transformedCriteria?.['bet-type'] ?? '');
        this.minOddsFrom.setValue(transformedCriteria?.['bet-min-odds'] ?? '');
        this.rewardValueForm.setValue(divideBy100(String(campaignModel.bonusRewards?.[0]?.rewardAmount ?? '')));
    };
}
